<template lang="html">
  <v-row no-gutters>
    <FlvSplitPercent
      v-model="splitPercent"
      :total-value="Number(data.flv)"
    />

    <v-col
      cols="12"
      class="text-h6 pb-0"
      mt-4
    >
      Member spouse
    </v-col>

    <template v-if="reportType === 'contributor'">
      <v-col
        class="no-print"
        cols="12"
      >
        <v-row align="center">
          <v-col
            cols="12"
            sm="6"
          >
            <SiInputSelect
              v-model="claimType"
              label="Type of benefit claimed"
              :items="claimTypes"
              return-object
              shaped
              outlined
              :disabled="splitPercent === 0"
              @change="handleClaimTypeChange"
            />
          </v-col>
          <v-col sm="6">
            <v-slider
              v-model="claimAge"
              label="Claim age"
              :min="memberAge.years"
              :max="maxClaimAge"
              thumb-label="always"
              hint="Estimated member age at date of claim"
              :disabled="splitPercent === 0"
              @end="handleClaimAgeChange"
            >
              <template #thumb-label="{ value }">
                {{ value }}{{ value === maxClaimAge ? '+' : '' }}
              </template>
            </v-slider>
          </v-col>
        </v-row>
      </v-col>

      <v-col class="only-print">
        <v-row class="">
          <v-col cols="6">
            Type of benefit claimed
            <div class="subtitle-1 font-weight-bold">
              {{ claimType.text }}
            </div>
          </v-col>
          <v-col cols="6">
            Estimated member age at claim (years)
            <div class="subtitle-1 font-weight-bold">
              {{ claimAge }}{{ claimAge === maxClaimAge ? '+' : '' }}
            </div>
          </v-col>
        </v-row>
      </v-col> 
    </template>


    <flv-values :values="memberValuesOne" />

    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_member_one"
        class="no-print"
        width="100%"
        :height="250"
        :options="memberChartOneOptions"
        :series="memberSeriesOne"
      />
    </v-col>

    <flv-values :values="memberValuesTwo" />

    <v-col
      v-if="memberSeriesTwo.length > 0"
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        v-if="memberSeriesTwo.length > 0"
        ref="graph_member_two"
        class="no-print"
        width="100%"
        :height="250"
        :options="memberChartTwoOptions"
        :series="memberSeriesTwo"
      />
    </v-col>

      
    <v-col
      class="text-h6 pb-0"
      mt-4
    >
      Non-member spouse
    </v-col>

    <flv-values :values="nonMemberValues" />
        
    <v-col
      cols="12"
      class="py-0 pb-0"
    >
      <apexcharts
        ref="graph_nonmember"
        class="no-print"
        width="100%"
        :height="250"
        :options="nonMemberChartOptions"
        :series="nonMemberSeries"
      />
    </v-col>
  </v-row>
</template>

<script>
import helpers from '@/mixins/helpers'
import VueApexCharts from 'vue-apexcharts'
import chartOptions from './chart-options'
import growthShared from './growth-shared'


export default {
  components: {
    apexcharts: VueApexCharts
  },
  mixins: [ helpers, chartOptions, growthShared ],
  props: {
    data: Object,
    tables: Object,
  },
  data () {
    return {
      splitPercent: 0,
      nonMemberSeries: [],
      memberSeriesOne: [],
      memberSeriesTwo: [],
      claimType: { text: 'Age Retirement (incl. redundancy) ', value: 'age' },
      claimAge: 0,
      maxClaimAge: 65
    }
  },
  computed: {
    labels () {
      return {
        nonMember: {
          separationAmount: 'Transfer amount',
          fundedComponent: 'Funded component of transfer amount',
          unfundedComponent: 'Unfunded component of transfer amount',
        },
        member: {
          benefitReductionRate: 'Benefit rate of reduction',
          fundedComponent: 'Funded component - post reduction',
          unfundedComponent: 'Unfunded component - post reduction',
          ap: 'Accrued Pension Multiple (AP) - reduction amount',
          apPost: 'Accrued Pension Multiple (AP) - post reduction',
          abc: 'Accumulated Basic Contributions (ABC) - post reduction',
          asc: 'Accumulated Supplementary Contributions (ASC) - post reduction',
          pci: 'Employer Productivity Contributions (PCI) - post reduction',
          mtv: 'Basic contributions in transfer values (MTV) - post reduction',
          ttv: 'Total amount of transfer values (TTV) - post reduction',
        }
      }
    },
    reportType () {
      let { data: { valuation_type } } = this
      if (['css_contributing_under65', 'css_contributing_over65'].includes(valuation_type)) return `contributor`
      if (['css_deferred_transfer', 'css_deferred_x_transfer'].includes(valuation_type)) return `deferred`
      if (['css_associate'].includes(valuation_type)) return `deferred_associate`
       return null
    },
    claimTypes () {
      return [
        { text: 'Age Retirement (incl. redundancy) ', value: 'age' },
        { text: 'Invalidity', value: 'invalid' },
      ]
    },
    memberAge () {
      let { data: { relevant_date, birth_date } } = this
      return this._calculateAge(birth_date, relevant_date)
    },
    ageFactor () {
      let pensionType = this.claimType.value
      let table = this.tables.age_factors || {}
      return (table[this.claimAge] || {})[pensionType]
    },
    fundedComponent () {
      let { funded_component, fls, valuation_type } = this.data
      return valuation_type === 'css_associate' ? fls : funded_component
    },
    memberValuesOne () {
      let { _$, labels, colors, reportType, data: { valuation_type } } = this 
      let sfGross = this.calcSeparationFactorGross()
      let primary = []
      let secondary = []

      switch (reportType) {
        case 'contributor':
          primary = [
            { label: labels.member.abc, value: _$(this.calcComponent('abc', sfGross), 0), color: colors[0]},
            { label: labels.member.asc, value: _$(this.calcComponent('asc', sfGross), 0), color: colors[1]},
            { label: labels.member.pci, value: _$(this.calcComponent('pci', sfGross), 0), color: colors[2]},
          ]
          break;
        case 'deferred':
          primary = [
            { label: labels.member.abc, value: _$(this.calcComponent('abc', sfGross), 0), color: colors[0] },
            { label: labels.member.asc, value: _$(this.calcComponent('asc', sfGross), 0), color: colors[1] },
            { label: labels.member.pci, value: _$(this.calcComponent('pci', sfGross), 0), color: colors[2] },
          ]
          if (valuation_type === 'css_deferred_transfer') {
            secondary = [
              { label: labels.member.mtv, value: _$(this.calcComponent('mtv', sfGross), 0) },
              { label: labels.member.ttv, value: _$(this.calcComponent('ttv', sfGross), 0) },
            ]
          }
          break;
        case 'deferred_associate':
          primary = [
            { label: labels.member.fundedComponent, value: _$(this.fundedComponent - this.calcFundedComponentSplit(), 0), color: colors[0] },
            { label: labels.member.unfundedComponent, value: _$(this.data.ufls - this.calcUnfundedComponentSplit(), 0), color: colors[1] },
          ]
          break;
        default: 
          primary = [
            { label: labels.member.fundedComponent, value: _$(this.fundedComponent - this.calcFundedComponentSplit(), 0), color: colors[0] },
          ]
      }
      return { primary, secondary }
    },
    memberValuesTwo () {
      let { labels, colors, reportType } = this 
      let sfGross = this.calcSeparationFactorGross()
      let primary = []
      let secondary = []

      switch (reportType) {
        case 'contributor':
          primary = [
            { label: labels.member.apPost, value: this.calcApPost(sfGross), color: colors[0] },
          ]
          secondary = [
            { label: labels.member.ap, value: this.calcApReduction(sfGross) },
          ]
        break;
        case 'deferred':
          // primary = [
          //   { label: labels.member.mtv, value: _$(this.calcComponent('mtv', sfGross), 0), color: colors[0] },
          //   { label: labels.member.ttv, value: _$(this.calcComponent('ttv', sfGross), 0), color: colors[1] },
          // ]
          break;
        case 'deferred_associate':
          break;
        default: 
            primary = [
              { label: labels.member.benefitReductionRate, value: this.calcBenefitReductionRate(sfGross), color: colors[0] },
            ]
      }
      return { primary, secondary }
    },
    memberChartOneOptions () {
      let yMax = undefined //this.calcTotalBenefitPost(0)

      return this._chartOptions({
        ...this.sharedChartOptions,
        id: 'graph_member_one',
        stacked: true,
        yMax
      })
    },
    memberChartTwoOptions () {
      let { data: { ap, ttv, mtv } } = this
      let yFormatter = v => this._$(v, 0)
      let yMax 
      switch (this.reportType) {
        case 'contributor': 
          yMax = ap
          yFormatter = v => v.toFixed(5)
          break;
        case 'deferred': 
          yMax = Math.max(ttv, mtv) 
          break;
        default:yMax = undefined
      }
      return this._chartOptions({
        ...this.sharedChartOptions,
        id: 'graph_member_two',
        yFormatter,
        yMax
      })
    },
    
  },
  beforeMount () {
    this.graphNonMember()
    this.graphMemberOne()
  },
  mounted () {
    this.graphMemberTwo()
  },
  methods: {
    graphMemberOne () {
      let { labels, reportType } = this

      let fundedSeries = { name: labels.member.fundedComponent, type: 'area', data: [] }
      let unfundedSeries = { name: labels.member.unfundedComponent, type: 'area', data: [] }
      let abcSeries = { name: labels.member.abc, type: 'area', data: [] }
      let ascSeries = { name: labels.member.asc, type: 'area', data: [] }
      let pciSeries = { name: labels.member.pci, type: 'area', data: [] }

      this.xValues.forEach(percent => {
        let separationAmount = this.calcSeparationAmount(percent)
        let sfGross = this.calcSeparationFactorGross(separationAmount)
        let fundedComponentSplit = this.calcFundedComponentSplit(sfGross)
        let unfundedComponentSplit = this.calcUnfundedComponentSplit(separationAmount, fundedComponentSplit)

        fundedSeries.data.push(this.fundedComponent - fundedComponentSplit)
        unfundedSeries.data.push(this.data.ufls - unfundedComponentSplit)
        
        abcSeries.data.push(this.calcComponent('abc', sfGross))
        ascSeries.data.push(this.calcComponent('asc', sfGross))
        pciSeries.data.push(this.calcComponent('pci', sfGross))
      })

      switch (reportType) {
        case 'contributor':
          this.memberSeriesOne = [ abcSeries, ascSeries, pciSeries ]
          break;
        case 'deferred':
          this.memberSeriesOne = [ abcSeries, ascSeries, pciSeries ]
          break;
        case 'deferred_associate':
          this.memberSeriesOne = [ fundedSeries, unfundedSeries ]
          break;
        default: 
          this.memberSeriesOne = [ fundedSeries ]
      }
    },
    graphMemberTwo () {
      let { labels, reportType, data: { valuation_type }  } = this
      // let benefitReductionRateSeries = { name: labels.member.benefitReductionRate, type: 'line', data: [] }
      let apSeries = { name: labels.member.apPost, type: 'line', data: [] }
      let mtvSeries = { name: labels.member.mtv, type: 'area', data: [] }
      let ttvSeries = { name: labels.member.ttv, type: 'area', data: [] }

      this.xValues.forEach(percent => {
        let separationAmount = this.calcSeparationAmount(percent)
        let sfGross = this.calcSeparationFactorGross(separationAmount)

        apSeries.data.push(this.calcApPost(sfGross))
        mtvSeries.data.push(this.calcComponent('mtv', sfGross))
        ttvSeries.data.push(this.calcComponent('ttv', sfGross))
      })

      switch (reportType) {
        case 'contributor':
            this.memberSeriesTwo = [ apSeries ]
          break;
        case 'deferred':
            if (valuation_type !== 'css_deferred_x_transfer') {
              this.memberSeriesTwo = [ mtvSeries, ttvSeries ]
            } else {
              this.memberSeriesTwo = [ ]
            }
          break;
        case 'deferred_associate':
            this.memberSeriesTwo = []
          break;
        default: 
          this.memberSeriesTwo = []
      }
    },
    calcComponent (compId, sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return (this.data[compId] || 0) * (1 - sfGross)
    },
    calcApPost (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return ((this.data.ap * this.ageFactor) - this.calcApReduction(sfGross)).toFixed(5)
    },
    calcApReduction (sfGross) {
      if (sfGross === undefined) sfGross = this.calcSeparationFactorGross()
      return (this.data.ap * sfGross * this.ageFactor).toFixed(5)
    },
    handleClaimAgeChange () {
      if (this.reportType === 'contributor') this.graphMemberTwo()
    },
    handleClaimTypeChange () {
      if (this.reportType === 'contributor') this.graphMemberTwo()
    }
  }
  
}
</script>

<style lang="css">

</style>
